/**
 * Photo Memories — live scan-cost estimate.
 *
 * Reuses the SAME catalog-price arithmetic the AI Costs / Pipeline Models pages
 * use — dollars = tokens / 1_000_000 × price-per-1M-tokens (see
 * quill_xai_models.pricing_amount, "per 1M input tokens", migration 477/525) —
 * applied to a documented per-photo token profile for the photo pipeline.
 *
 * This is a PLANNING estimate shown beside the scan_limit control, NOT a billing
 * figure. The real spend is metered per-run into quill_ai_usage_log and shown as
 * `cost_actual_usd` in the run-history table; this estimate exists only to give
 * the admin a sense of scale before raising N. Calibrated so N=100 lands in the
 * plan's ~$2–$4 band (mid ≈ $3).
 *
 * The token/price constants below are coarse assumptions, exported so a future
 * refinement (or a test) can adjust them in one place.
 */

/** Fraction of scanned photos that pass the on-device people/animal gate and get
 *  uploaded + captioned. The rest are skipped on-device at zero AI cost. */
export const GATE_PASS_RATE = 0.7;

/** Per-1M-token prices (USD), mirroring the catalog seed for a vision-capable
 *  model (claude-sonnet-5: $3 in / $15 out, migration 525). Same unit the cost
 *  pages read from quill_xai_models.pricing_amount. */
export const PRICE_PER_1M = { input: 3.0, output: 15.0 };

/** Per-captioned-photo vision-caption token profile (image tiles + prompt → caption). */
export const CAPTION_TOKENS = { input: 1600, output: 220 };

/** Per-captioned-photo synthesis + skeptic-verification token profile (the
 *  captions are re-read to draft memories and to reject unsupported facts). */
export const SYNTHESIS_TOKENS = { input: 8000, output: 800 };

/** Half-width of the estimate band as a fraction of the mid estimate. The mid is
 *  a point calibration; real cost varies with photo content and gate pass rate,
 *  so the surface shows a range, never a false-precision single number. */
export const BAND_FRACTION = 0.33;

export interface CostEstimate {
  /** Number of scanned photos the estimate is for. */
  scanLimit: number;
  /** Expected count that pass the gate and are actually captioned. */
  captionedPhotos: number;
  /** Low / mid / high USD estimate. */
  lowUsd: number;
  midUsd: number;
  highUsd: number;
}

/** dollars = tokens / 1_000_000 × price-per-1M (the cost pages' formula). */
function tokenCostUsd(tokens: { input: number; output: number }): number {
  return (tokens.input / 1_000_000) * PRICE_PER_1M.input + (tokens.output / 1_000_000) * PRICE_PER_1M.output;
}

/**
 * Estimate the AI cost of a photo scan of `scanLimit` photos. Pure + deterministic.
 * Returns zeros for a non-positive / non-finite input rather than NaN.
 */
export function estimatePhotoScanCost(scanLimit: number): CostEstimate {
  const n = Number.isFinite(scanLimit) && scanLimit > 0 ? Math.floor(scanLimit) : 0;
  const captionedPhotos = Math.round(n * GATE_PASS_RATE);

  const perCaptioned = tokenCostUsd(CAPTION_TOKENS) + tokenCostUsd(SYNTHESIS_TOKENS);
  const midUsd = captionedPhotos * perCaptioned;

  return {
    scanLimit: n,
    captionedPhotos,
    lowUsd: midUsd * (1 - BAND_FRACTION),
    midUsd,
    highUsd: midUsd * (1 + BAND_FRACTION),
  };
}

/** Format a USD amount for the estimate band (2 decimals, $ prefix). */
export function formatUsd(v: number): string {
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
}

/** "$2.10 – $4.11" style range string for the scan-limit helper text. */
export function formatEstimateRange(est: CostEstimate): string {
  return `${formatUsd(est.lowUsd)} – ${formatUsd(est.highUsd)}`;
}
